import React, { useEffect, useState } from "react";
import { getAuth } from "firebase/auth";
import { addDoc, collection, doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { callJoinFamilyByCode, needsKidReplace } from "../utils/joinFamilyByCode";
import { useToast } from "./ui/ToastProvider";
import { useConfirm } from "./ui/ConfirmProvider";
import { mapFirebaseError } from "../utils/userErrors";

export default function FirstTimeSetup({ onFamilyReady }) {
  const auth = getAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const [familyName, setFamilyName] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [joinName, setJoinName] = useState("");
  const [loading, setLoading] = useState(false);
  const [createError, setCreateError] = useState("");
  const [joinError, setJoinError] = useState("");

  useEffect(() => {
    document.body.className = "theme-ocean";
  }, []);

  const createFamily = async () => {
    if (!familyName.trim()) {
      setCreateError("Family name is required.");
      return;
    }
    setLoading(true);
    setCreateError("");
    setJoinError("");
    try {
      const timezone = typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
      const starterLayout = [
        "setupChecklist",
        "tasks",
        "repeats",
        "approvals",
        "clarifications",
        "calendar",
        "settingsOverview"
      ];
      const cardSizes = {
        tasks: "large",
        approvals: "large",
        clarifications: "large"
      };
      const docRef = await addDoc(collection(db, "families"), {
        pin: "1234",
        theme: "ocean",
        isPro: false,
        paidPro: false,
        complimentaryPro: false,
        subscriptionStatus: null,
        subscriptionPlan: null,
        subscriptionCancelAtPeriodEnd: false,
        subscriptionRenewAt: null,
        subscriptionExpiresAt: null,
        stripeCustomerId: null,
        stripeSubscriptionId: null,
        ownerUid: auth.currentUser.uid,
        familyName: familyName.trim(),
        memberUids: [auth.currentUser.uid],
        timezone,
        dashboardLayout: starterLayout,
        cardSizes
      });
      await setDoc(doc(db, "families", docRef.id, "members", auth.currentUser.uid), {
        role: "parent",
        displayName: auth.currentUser.displayName || auth.currentUser.email?.split("@")[0] || "Parent",
        joinedAt: new Date()
      });
      showToast({ type: "success", message: "Family created." });
      onFamilyReady(docRef.id);
    } catch (err) {
      setCreateError(mapFirebaseError(err, "Unable to create family."));
    }
    setLoading(false);
  };

  const joinFamily = async () => {
    if (!joinName.trim()) {
      setJoinError("Username is required.");
      return;
    }
    if (!joinCode.trim()) {
      setJoinError("Invite code is required.");
      return;
    }
    setLoading(true);
    setCreateError("");
    setJoinError("");
    try {
      try {
        const data = await callJoinFamilyByCode({
          code: joinCode.trim(),
          name: joinName.trim()
        });
        showToast({ type: "success", message: "Joined family." });
        onFamilyReady(data.familyId);
      } catch (error) {
        if (needsKidReplace(error)) {
          const proceed = await confirm({
            title: "Replace existing kid login?",
            message:
              "A kid with this name already exists. Joining will replace their login and keep their data.",
            confirmLabel: "Replace and join",
            cancelLabel: "Cancel",
            danger: true
          });
          if (!proceed) {
            setLoading(false);
            return;
          }
          const data = await callJoinFamilyByCode({
            code: joinCode.trim(),
            name: joinName.trim(),
            forceReplace: true
          });
          showToast({ type: "success", message: "Joined family." });
          onFamilyReady(data.familyId);
        } else {
          setJoinError(mapFirebaseError(error, "Unable to join family."));
        }
      }
    } catch (err) {
      setJoinError(mapFirebaseError(err, "Unable to join family."));
    }
    setLoading(false);
  };

  return (
    <div className="first-run">
      <div className="glass first-run-card">
        <h1>Welcome to FamilySync</h1>
        <p className="first-run-subtitle">
          Let’s get your family set up. We’ll guide you through the first few steps after this.
        </p>

        <ol className="first-run-steps">
          <li>Create a family or join one with an invite code.</li>
          <li>Add parents and kids under Settings → Family.</li>
          <li>Build chores in the Parent Hub and assign them.</li>
        </ol>

        <div className="first-run-actions">
          <div className="first-run-panel">
            <h3>Create a Family</h3>
            <input
              placeholder="Family name"
              value={familyName}
              onChange={(e) => setFamilyName(e.target.value)}
            />
            {createError && (
              <small className="pin-error" role="alert">
                {createError}
              </small>
            )}
            <button
              className="btn-nav success-btn"
              onClick={createFamily}
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Creating..." : "Create Family"}
            </button>
          </div>

          <div className="first-run-panel">
            <h3>Join with a Code</h3>
            <input
              placeholder="Username"
              value={joinName}
              onChange={(e) => setJoinName(e.target.value)}
            />
            <input
              placeholder="Parent or kid invite code"
              value={joinCode}
              onChange={(e) => setJoinCode(e.target.value)}
            />
            {joinError && (
              <small className="pin-error" role="alert">
                {joinError}
              </small>
            )}
            <button
              className="btn-nav"
              onClick={joinFamily}
              disabled={loading}
              style={{ width: "100%" }}
            >
              {loading ? "Joining..." : "Join Family"}
            </button>
          </div>
        </div>

        <p className="first-run-note">
          Tip: The first family you create becomes your default family. You can
          add more families later from Settings → Family. Default parent PIN is <strong>1234</strong>
          (and the younger child PIN default is also <strong>1234</strong> until changed).
        </p>
      </div>
    </div>
  );
}
